import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ZoomIn, ZoomOut, RotateCw, RotateCcw, Maximize2 } from 'lucide-react'

interface ImageViewerProps {
  imageUrl: string
  fileName?: string
}

export default function ImageViewer({ imageUrl, fileName }: ImageViewerProps) {
  const { t } = useTranslation()
  const [zoom, setZoom] = useState<number>(100)
  const [rotation, setRotation] = useState<number>(0)

  // Zoom controls
  const zoomIn = () => setZoom((prev) => Math.min(300, prev + 25))
  const zoomOut = () => setZoom((prev) => Math.max(25, prev - 25))
  const resetZoom = () => {
    setZoom(100)
    setRotation(0)
  }

  // Rotation controls
  const rotateRight = () => setRotation((prev) => (prev + 90) % 360)
  const rotateLeft = () => setRotation((prev) => (prev - 90 + 360) % 360)

  return (
    <div className="flex flex-col h-full bg-base-100">
      {/* Controls */}
      <div className="flex items-center justify-between gap-4 px-4 py-3 bg-base-100 border-b border-base-300">
        <div className="text-sm font-medium text-base-content/70">
          {fileName}
        </div>

        {/* Zoom and Rotation Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={rotateLeft}
            className="btn btn-sm btn-ghost btn-circle"
            title={t('imageViewer.controls.rotateLeft')}
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            onClick={rotateRight}
            className="btn btn-sm btn-ghost btn-circle"
            title={t('imageViewer.controls.rotateRight')}
          >
            <RotateCw className="w-4 h-4" />
          </button>

          <div className="h-4 w-px bg-base-300 mx-1"></div>

          <button
            onClick={zoomOut}
            disabled={zoom <= 25}
            className="btn btn-sm btn-ghost btn-circle"
            title={t('imageViewer.controls.zoomOut')}
          >
            <ZoomOut className="w-4 h-4" />
          </button>

          <div className="px-3 text-sm font-medium text-base-content/70 min-w-[60px] text-center">
            {Math.round(zoom)}%
          </div>

          <button
            onClick={zoomIn}
            disabled={zoom >= 300}
            className="btn btn-sm btn-ghost btn-circle"
            title={t('imageViewer.controls.zoomIn')}
          >
            <ZoomIn className="w-4 h-4" />
          </button>

          <button
            onClick={resetZoom}
            className="btn btn-sm btn-ghost btn-circle"
            title={t('imageViewer.controls.resetView')}
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Image Display */}
      <div className="flex-1 overflow-auto bg-base-200">
        <div className="flex items-center justify-center min-h-full p-8">
          <img
            src={imageUrl}
            alt={fileName || 'Image preview'}
            className="shadow-lg"
            style={{
              transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
              transition: 'transform 0.2s ease-in-out',
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain'
            }}
          />
        </div>
      </div>
    </div>
  )
}
